import React from 'react';
import { Box, Typography, Button, Chip, Divider, SxProps, Theme } from '@mui/material';
import { Project } from '../../types';
import {
    getProjectedReleaseDate,
    getProjectVersion,
    getProjectStateLabel,
    isMinorProject,
    isProjectDelayed,
    isInProgressProjectState,
    isInPlanningProjectState,
    isReleasedProjectState,
} from '../../utils';

interface VersionFilterProps {
    projects: Project[];
    selectedProjectId: string;
    onProjectClick: (project: Project) => void;
}

const FLOW_TRACK_HEIGHT = 42;

const FLOW_BUTTON_HEIGHT = {
    normal: 42,
    minor: 30,
};

const CONNECTOR_WIDTH = { xs: 36, sm: 52, md: 68 };

/** Keeps buttons aligned when only some projects have metadata lines */
const PROJECT_META_SLOT_HEIGHT = 34;
const RELEASE_DATE_SLOT_MARGIN = 2;
const FLOW_CONNECTOR_TOP_OFFSET = PROJECT_META_SLOT_HEIGHT + RELEASE_DATE_SLOT_MARGIN;

const getStateChipSx = (state: string, compact: boolean): SxProps<Theme> => {
    const normalized = state.toLowerCase();
    const isCompleted = normalized === 'completed';
    const isActive = normalized === 'active' || normalized === 'started' || normalized === 'in-progress';
    const isPlanned = normalized === 'planned';
    const isPaused = normalized === 'paused';

    return {
        fontSize: compact ? '0.58rem' : '0.65rem',
        height: compact ? '16px' : '18px',
        backgroundColor: isCompleted ? 'rgba(76, 175, 80, 0.2)' :
            isActive ? 'rgba(255, 193, 7, 0.2)' :
                isPaused ? 'rgba(255, 152, 0, 0.2)' :
                    isPlanned ? 'rgba(255, 255, 255, 0.08)' :
                        'rgba(255, 255, 255, 0.1)',
        color: isCompleted ? '#4caf50' :
            isActive ? '#ffc107' :
                isPaused ? '#ff9800' :
                    'text.secondary',
        border: '1px solid',
        borderColor: isCompleted ? '#4caf50' :
            isActive ? '#ffc107' :
                isPaused ? '#ff9800' :
                    'rgba(255, 255, 255, 0.2)',
    };
};

type FlowConnectorVariant = 'released' | 'warning' | 'default';

interface FlowConnectorProps {
    variant: FlowConnectorVariant;
}

const FLOW_CONNECTOR_STYLES: Record<FlowConnectorVariant, { line: string; shadow: string; arrow: string }> = {
    released: {
        line: 'linear-gradient(90deg, rgba(76, 175, 80, 0.95), rgba(76, 175, 80, 0.75))',
        shadow: '0 0 6px rgba(76, 175, 80, 0.35)',
        arrow: 'rgba(76, 175, 80, 0.9)',
    },
    warning: {
        line: 'linear-gradient(90deg, rgba(255, 193, 7, 0.95), rgba(255, 193, 7, 0.72))',
        shadow: '0 0 6px rgba(255, 193, 7, 0.32)',
        arrow: 'rgba(255, 193, 7, 0.9)',
    },
    default: {
        line: 'linear-gradient(90deg, rgba(158, 158, 158, 0.75), rgba(158, 158, 158, 0.55))',
        shadow: 'none',
        arrow: 'rgba(158, 158, 158, 0.85)',
    },
};

const getFlowConnectorVariant = (from: Project, to: Project): FlowConnectorVariant => {
    if (isReleasedProjectState(from.state) && isReleasedProjectState(to.state)) {
        return 'released';
    }

    if (isReleasedProjectState(from.state) && isInProgressProjectState(to.state)) {
        return 'warning';
    }

    if (
        isInProgressProjectState(from.state)
        && (isInProgressProjectState(to.state) || isInPlanningProjectState(to.state))
    ) {
        return 'default';
    }

    return 'default';
};

const FlowConnector: React.FC<FlowConnectorProps> = ({ variant }) => (
    <Box
        aria-hidden
        sx={{
            flexShrink: 0,
            width: CONNECTOR_WIDTH,
            height: FLOW_TRACK_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            alignSelf: 'flex-start',
            mt: `${FLOW_CONNECTOR_TOP_OFFSET}px`,
            position: 'relative',
            zIndex: 1,
        }}
    >
        <Box
            sx={{
                width: 'calc(100% - 5px)',
                height: 2,
                position: 'relative',
                background: FLOW_CONNECTOR_STYLES[variant].line,
                boxShadow: FLOW_CONNECTOR_STYLES[variant].shadow,
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    right: -2,
                    top: '50%',
                    width: 7,
                    height: 7,
                    transform: 'translateY(-50%) rotate(45deg)',
                    borderRight: `2px solid ${FLOW_CONNECTOR_STYLES[variant].arrow}`,
                    borderTop: `2px solid ${FLOW_CONNECTOR_STYLES[variant].arrow}`,
                },
            }}
        />
    </Box>
);

const VersionFilter: React.FC<VersionFilterProps> = ({
    projects,
    selectedProjectId,
    onProjectClick
}) => {
    const getProjectIcon = (projectState?: string) => {
        const normalizedState = projectState?.toLowerCase();
        if (normalizedState === 'completed') return '✅';
        if (normalizedState === 'active' || normalizedState === 'started' || normalizedState === 'in-progress') return '🛠️';
        return '📋';
    };

    const firstStartedProjectId = projects.find(
        project => project.state.toLowerCase() === 'started'
    )?.id;

    return (
        <Box sx={{ mt: 3, mb: 1 }}>
            <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                    mb: 2,
                    textAlign: 'center',
                    fontStyle: 'italic',
                    opacity: 0.8,
                    fontSize: '0.85rem'
                }}
            >
                Click on a project to filter issues and view details in the sidebar
            </Typography>

            {/* Single horizontal row — no wrap, so buttons/lines/chips never scatter */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'nowrap',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    width: '100%',
                    overflowX: 'auto',
                    pb: 0.5,
                    '&::-webkit-scrollbar': { height: 4 },
                    '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(63, 81, 181, 0.4)',
                        borderRadius: 2,
                    },
                }}
            >
                {projects.map((project, index) => {
                    const isSelected = selectedProjectId === project.id;
                    const isMinor = isMinorProject(project);
                    const buttonHeight = isMinor ? FLOW_BUTTON_HEIGHT.minor : FLOW_BUTTON_HEIGHT.normal;
                    const projectedRelease = getProjectedReleaseDate(project);
                    const projectVersion = getProjectVersion(project);
                    const isFirstStarted = project.id === firstStartedProjectId;
                    const isReleased = isReleasedProjectState(project.state);
                    const isPlanned = isInPlanningProjectState(project.state);
                    const isDelayed = isProjectDelayed(project);

                    return (
                        <React.Fragment key={project.id}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                <Box
                                    sx={{
                                        minHeight: PROJECT_META_SLOT_HEIGHT,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'flex-end',
                                        gap: 0.25,
                                        mb: `${RELEASE_DATE_SLOT_MARGIN}px`,
                                        width: '100%',
                                    }}
                                >
                                    {projectedRelease && (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontSize: '0.65rem',
                                                lineHeight: 1.2,
                                                color: isDelayed ? '#f44336' : (isFirstStarted ? '#fff' : 'text.secondary'),
                                                fontWeight: isFirstStarted ? 700 : 400,
                                                opacity: isFirstStarted ? 1 : 0.85,
                                                whiteSpace: 'nowrap',
                                                textAlign: 'center',
                                            }}
                                        >
                                            {projectedRelease}
                                        </Typography>
                                    )}
                                    {projectVersion && (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontSize: '0.62rem',
                                                lineHeight: 1.2,
                                                color: 'text.secondary',
                                                fontWeight: 500,
                                                opacity: 0.9,
                                                whiteSpace: 'nowrap',
                                                textAlign: 'center',
                                            }}
                                        >
                                            {`Version: ${projectVersion}`}
                                        </Typography>
                                    )}
                                </Box>
                                <Box
                                    sx={{
                                        height: FLOW_TRACK_HEIGHT,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Button
                                        variant={isSelected ? 'contained' : 'outlined'}
                                        onClick={() => onProjectClick(project)}
                                        sx={{
                                            borderRadius: isMinor ? 2 : 3,
                                            px: isMinor ? 1.5 : 3,
                                            py: isMinor ? 0.35 : 1,
                                            minHeight: buttonHeight,
                                            height: buttonHeight,
                                            fontSize: isMinor ? '0.72rem' : '0.9rem',
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            position: 'relative',
                                            cursor: 'pointer',
                                            m: 0,
                                            whiteSpace: 'nowrap',
                                            flexShrink: 0,
                                            background: isSelected
                                                ? 'linear-gradient(135deg, rgba(63, 81, 181, 0.9), rgba(48, 63, 159, 0.9))'
                                                : isReleased
                                                    ? 'linear-gradient(135deg, rgba(165, 214, 167, 0.55), rgba(129, 199, 132, 0.45))'
                                                    : isPlanned
                                                        ? 'linear-gradient(135deg, rgba(18, 18, 18, 0.92), rgba(8, 8, 8, 0.95))'
                                                        : 'rgba(255, 255, 255, 0.05)',
                                            backdropFilter: 'blur(15px)',
                                            border: isSelected
                                                ? '1px solid rgba(63, 81, 181, 0.6)'
                                                : isReleased
                                                    ? '1px solid rgba(165, 214, 167, 0.85)'
                                                    : isPlanned
                                                        ? '1px solid rgba(45, 45, 45, 0.9)'
                                                        : '1px solid rgba(255, 255, 255, 0.2)',
                                            color: isSelected
                                                ? 'white'
                                                : isReleased
                                                    ? '#e8f5e9'
                                                    : isPlanned
                                                        ? '#9e9e9e'
                                                        : 'text.primary',
                                            boxShadow: isSelected
                                                ? '0 4px 15px rgba(63, 81, 181, 0.3), 0 0 20px rgba(63, 81, 181, 0.1)'
                                                : isReleased
                                                    ? '0 4px 15px rgba(129, 199, 132, 0.25), 0 0 20px rgba(165, 214, 167, 0.15)'
                                                    : isPlanned
                                                        ? '0 4px 14px rgba(0, 0, 0, 0.55)'
                                                        : '0 2px 10px rgba(0, 0, 0, 0.1)',
                                            transition: 'background 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
                                            '&:hover': {
                                                background: isSelected
                                                    ? 'linear-gradient(135deg, rgba(63, 81, 181, 1), rgba(48, 63, 159, 1))'
                                                    : isReleased
                                                        ? 'linear-gradient(135deg, rgba(165, 214, 167, 0.7), rgba(129, 199, 132, 0.6))'
                                                        : isPlanned
                                                            ? 'linear-gradient(135deg, rgba(28, 28, 28, 0.95), rgba(12, 12, 12, 0.98))'
                                                            : 'rgba(255, 255, 255, 0.15)',
                                                boxShadow: isSelected
                                                    ? '0 6px 20px rgba(63, 81, 181, 0.4), 0 0 30px rgba(63, 81, 181, 0.2)'
                                                    : isReleased
                                                        ? '0 6px 20px rgba(129, 199, 132, 0.35), 0 0 25px rgba(165, 214, 167, 0.2)'
                                                        : isPlanned
                                                            ? '0 6px 18px rgba(0, 0, 0, 0.65)'
                                                            : '0 4px 15px rgba(255, 255, 255, 0.1), 0 0 25px rgba(255, 255, 255, 0.05)',
                                                borderColor: isSelected
                                                    ? 'rgba(63, 81, 181, 0.8)'
                                                    : isReleased
                                                        ? 'rgba(200, 230, 201, 0.95)'
                                                        : isPlanned
                                                            ? 'rgba(60, 60, 60, 0.95)'
                                                            : 'rgba(255, 255, 255, 0.4)',
                                            },
                                            '&::before': {
                                                content: '""',
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                borderRadius: isMinor ? 2 : 3,
                                                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0))',
                                                opacity: isSelected ? 1 : 0.7,
                                                transition: 'opacity 0.3s ease',
                                                pointerEvents: 'none',
                                            },
                                        }}
                                    >
                                        <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            {getProjectIcon(project.state)}
                                            {project.name}
                                        </Box>
                                    </Button>
                                </Box>

                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexWrap: 'nowrap',
                                        gap: 0.5,
                                        mt: 0.5,
                                        minHeight: 20,
                                        width: 'max-content',
                                    }}
                                >
                                    <Chip
                                        label={getProjectStateLabel(project.state)}
                                        size="small"
                                        sx={getStateChipSx(project.state, isMinor)}
                                    />
                                    {isMinor && (
                                        <Chip
                                            label="minor update"
                                            size="small"
                                            sx={{
                                                fontSize: '0.58rem',
                                                height: '16px',
                                                backgroundColor: 'rgba(156, 39, 176, 0.2)',
                                                color: '#ce93d8',
                                                border: '1px solid rgba(156, 39, 176, 0.45)',
                                            }}
                                        />
                                    )}
                                </Box>
                            </Box>

                            {index < projects.length - 1 && (
                                <FlowConnector
                                    variant={getFlowConnectorVariant(project, projects[index + 1])}
                                />
                            )}
                        </React.Fragment>
                    );
                })}
            </Box>

            <Divider sx={{ mt: 2, borderColor: '#333' }} />
        </Box>
    );
};

export default VersionFilter;
